<template>
  <div class="home-page">
    <!-- ============ 顶部搜索 + 热门城市 ============ -->
    <div class="home-topbar" v-if="homeContentVisible && compatFeatures.extras">
      <div class="topbar-search" @click="openCityPicker">
        <el-icon class="search-icon"><Search /></el-icon>
        <span>{{ t('weather.inputCity') }}</span>
      </div>
      <div class="topbar-hot-wrap">
        <div class="topbar-hot" ref="hotCityScrollerRef" @scroll="updateHotCityScroll">
          <button
            class="hot-city"
            v-for="hc in hotCityList"
            :key="hc.id"
            :class="{ active: currentCity?.id === hc.id }"
            @click="selectHotCity(hc)"
          >
            <img
              v-if="hotCityWeather[hc.id]?.icon != null"
              :src="staticURL(`/weather/w${hotCityWeather[hc.id].icon}.png`)"
              class="hc-icon"
              :alt="hc.name"
              onerror="this.style.display='none'"
            />
            <span class="hc-name">{{ hc.name }}</span>
            <span v-if="hotCityWeather[hc.id]?.temp != null" class="hc-temp">
              {{ hotCityWeather[hc.id].temp }}°
            </span>
          </button>
        </div>
        <div class="topbar-arrows">
          <button class="topbar-arrow topbar-arrow-prev" @click="scrollHotCity(-1)" :disabled="!hotCityCanPrev" aria-label="prev">
            <img src="@/assets/images/icon/btn-left.png" alt="" />
          </button>
          <button class="topbar-arrow topbar-arrow-next" @click="scrollHotCity(1)" :disabled="!hotCityCanNext" aria-label="next">
            <img src="@/assets/images/icon/btn-right.png" alt="" />
          </button>
        </div>
      </div>
    </div>

    <!-- 左侧浮动导航（固定布局：悬停展开，离开自动收起） -->
    <div class="floating-nav" v-if="homeContentVisible">
      <div class="fn-panel">
        <div class="fn-menu">
          <a
            v-for="item in navItems"
            :key="item.key"
            :href="`#${item.key}`"
            class="fn-item"
            :class="{ active: activeSection === item.key }"
            @click.prevent="scrollToSection(item.key)"
          >
            <img :src="item.icon" alt="" />
            <span>{{ item.label }}</span>
          </a>
        </div>
        <div class="fn-divider"></div>
        <div class="fn-actions">
          <button type="button" class="fn-action-btn fn-action-btn--top" @click="scrollToTop" title="置顶">
            <img :src="staticURL('/images/nav-top-btn.svg')" alt="" />
          </button>
          <button type="button" class="fn-action-btn fn-action-btn--refresh" @click="refreshPage" title="刷新">
            <img :src="staticURL('/images/nav-refresh.svg')" alt="" />
          </button>
        </div>
      </div>
      <div class="fn-rail" aria-hidden="true">
        <span
          v-for="item in navItems"
          :key="item.key"
          class="fn-dot"
          :class="{ active: activeSection === item.key }"
          :title="item.label"
        >
          <img v-if="activeSection === item.key" :src="item.icon" :alt="item.label" />
        </span>
      </div>
    </div>

    <!-- 响应式：仅回到顶部 -->
    <button
      v-if="homeContentVisible"
      type="button"
      class="back-top-btn"
      :class="{ 'is-visible': showBackTopBtn }"
      @click="scrollToTop"
      title="置顶"
      aria-label="回到顶部"
    >
      <img :src="staticURL('/images/nav-top-btn.svg')" alt="" />
    </button>

    <div class="home-wrap" v-if="homeContentVisible">
      <!-- ============ 主区 ============ -->
      <div class="home-main">
        <!-- Hero 当前天气 -->
        <div class="card hero" id="sec-current">
          <p v-if="compatFeatures.forceHomeShell" class="compat-shell-banner">
            基础版 L3：首页骨架已展示，未请求天气 API
          </p>
          <div v-if="status === -1" class="hero-loading" aria-label="加载中" role="status"></div>
          <error :status="status" v-if="(status == 1 || status == 2) && !iosUpgradeOverlayVisible" style="margin-top: 20px"></error>

          <div class="hero-body" v-if="status == 0 && weatherUiReady">
            <div class="hero-left">
              <div class="hero-top">
                <span class="hero-city" @click="openCityPicker">
                  <span class="hero-city-label">
                    <img class="loc-icon" :src="staticURL('/images/icon-location.svg')" alt="" />
                    <span
                      class="hero-city-name"
                      :title="locale == 'zh-CN' ? currentCity?.name : currentCity?.enname"
                    >{{ heroCityDisplayName }}</span>
                  </span>
                  <img class="arrow-icon" :src="staticURL('/images/icon-arrow-down.svg')" alt="" />
                </span>
              </div>

              <div class="hero-main">
                <div class="hero-intro">
                  <img
                    class="hero-icon"
                    v-if="currentData?.icon != null"
                    :src="staticURL(`/weather/w${currentData.icon}.png`)"
                    :alt="currentData?.weather || ''"
                    onerror="this.style.visibility='hidden'"
                  />
                  <div class="hero-info">
                    <div class="hero-temp">
                      <span class="temp-num">{{ currentData?.temp ?? '--' }}</span>
                      <span class="temp-unit">
                        <span class="deg-mark"></span>
                        <span
                          class="weather-text"
                          :class="{ 'is-wrap': heroWeatherTextWrap }"
                        >{{ currentData?.weather || '' }}</span>
                      </span>
                    </div>
                    <div class="hero-meta">
                      {{ currentData?.wind_dir }} {{ currentData?.wind_level }}{{ t('weather.level') }}
                      ｜ {{ t('weather.humidity') }} {{ currentData?.humidity ?? 0 }}%
                    </div>
                  </div>
                </div>
                <div class="hero-tags">
                  <span class="tag tag-aqi" v-if="aqiData?.aqi" @click="openAqi">
                    <span class="icon-ring" :style="{ '--ring-color': aqiInfo?.color }"></span>
                    {{ aqiInfo?.text }} {{ aqiData.aqi }}
                  </span>
                  <span
                    v-if="alertList.length"
                    class="tag tag-alert"
                    :class="`tag-alert-${alertLevelKey(currentAlert?.alert_level)}`"
                    :title="currentAlert?.title"
                  >
                    <span class="alert-icon" aria-hidden="true"></span>
                    <template v-if="alertList.length > 1">
                      <span class="alert-text-viewport">
                        <span
                          class="alert-text-track"
                          :class="{ 'is-resetting': alertTrackResetting }"
                          :style="{ transform: `translateY(-${alertIndex * ALERT_ITEM_HEIGHT}px)` }"
                          @transitionend="onAlertTrackTransitionEnd"
                        >
                          <span
                            v-for="(a, i) in alertTrackItems"
                            :key="`${a.alert_id}-${i}`"
                            class="alert-text-item"
                          >{{ formatAlert(a) }}</span>
                        </span>
                      </span>
                    </template>
                    <span v-else class="alert-text-item">{{ formatAlert(currentAlert) }}</span>
                  </span>
                </div>
              </div>
            </div>

            <div class="hero-right">
              <div class="radar-box" ref="radarBoxRef">
                <iframe
                  v-if="radarVisible && radarSrc && radarIframeEnabled"
                  ref="radarIframeRef"
                  :src="radarSrc"
                  class="radar-iframe"
                  :class="{ 'is-loaded': radarIframeLoaded }"
                  frameborder="0"
                  @load="onRadarIframeLoad"
                ></iframe>
                <IosUpgradeHint v-else-if="radarVisible && cityLon && iosUpgradeRequired" compact />
              </div>
            </div>
          </div>

          <!-- 6 个圆形指标 -->
          <div class="hero-metrics" v-if="status == 0 && weatherUiReady">
            <div class="hero-metric-item">
              <div class="val">{{ aqiInfo?.text || '—' }} {{ aqiData?.aqi ?? '' }}</div>
              <div class="lbl">{{ t('weather.air') }}</div>
            </div>
            <div class="hero-metric-item">
              <div class="val">
                {{ currentData?.wind_dir }} {{ currentData?.wind_level
                }}{{ t('weather.level') }}
              </div>
              <div class="lbl">{{ t('weather.wind') }}</div>
            </div>
            <div class="hero-metric-item">
              <div class="val">{{ currentData?.real_feel != null ? `${currentData.real_feel}°` : '--' }}</div>
              <div class="lbl">{{ t('weather.realFeel') }}</div>
            </div>
            <div class="hero-metric-item">
              <div class="val">{{ visValue ?? '--' }}</div>
              <div class="lbl">{{ t('weather.visibility') }}</div>
            </div>
            <div class="hero-metric-item">
              <div class="val">{{ currentData?.mslp ?? '--' }} hPa</div>
              <div class="lbl">{{ t('weather.airPressure') }}</div>
            </div>
            <div class="hero-metric-item">
              <div class="val">
                {{ currentData?.uvi != null ? getStrengthLevel(currentData.uvi) : '--' }}
              </div>
              <div class="lbl">{{ t('weather.uvIndex') }}</div>
            </div>
          </div>
        </div>

        <!-- 生活指数 -->
        <div class="card" id="sec-life" v-if="status == 0 && lifeIndexList.length && compatFeatures.lifeIndex">
          <div class="card-header">
            <h2 class="card-title">{{ t('forecast.service') }}</h2>
          </div>
          <div class="life-index-grid">
            <div
              class="life-index-viewport"
              :class="{ 'is-life-scrolling': lifeScrolling }"
              ref="lifeViewportEl"
              @scroll="onLifeScroll"
            >
              <div class="life-index-rows">
                  <div class="life-index-row">
                    <div
                      class="life-index-item"
                      v-for="item in lifeIndexLayout.topRow"
                      :key="'r1-' + item.index_type_id"
                      :class="{ active: !lifeScrolling && activeLifeId === item.index_type_id }"
                      @click="selectLifeItem(item)"
                    >
                      <div class="li-icon">
                        <img
                          :src="staticURL(`/serviceIcon/${getLifeIndexIconId(item)}.png`)"
                          :alt="item.index_type"
                          onerror="this.style.display='none'"
                        />
                      </div>
                      <div class="li-text">
                        <div class="li-level">{{ item.index_level_desc }}</div>
                        <div class="li-name">{{ item.index_type }}</div>
                      </div>
                    </div>
                    <div
                      class="life-index-item placeholder"
                      v-for="n in lifeIndexLayout.topPad"
                      :key="'r1-pad-' + n"
                    ></div>
                  </div>
                  <div class="life-index-row" v-if="lifeIndexLayout.bottomRow.length || lifeIndexLayout.bottomPad">
                    <div
                      class="life-index-item"
                      v-for="item in lifeIndexLayout.bottomRow"
                      :key="'r2-' + item.index_type_id"
                      :class="{ active: !lifeScrolling && activeLifeId === item.index_type_id }"
                      @click="selectLifeItem(item)"
                    >
                      <div class="li-icon">
                        <img
                          :src="staticURL(`/serviceIcon/${getLifeIndexIconId(item)}.png`)"
                          :alt="item.index_type"
                          onerror="this.style.display='none'"
                        />
                      </div>
                      <div class="li-text">
                        <div class="li-level">{{ item.index_level_desc }}</div>
                        <div class="li-name">{{ item.index_type }}</div>
                      </div>
                    </div>
                    <div
                      class="life-index-item placeholder"
                      v-for="n in lifeIndexLayout.bottomPad"
                      :key="'r2-pad-' + n"
                    ></div>
                  </div>
                </div>
            </div>
            <!-- 移动端滚动进度条（参考15天预报样式） -->
            <div v-if="lifeIndexCanScroll && showMobileIndicators" class="life-scroll-track">
              <div class="life-scroll-thumb" :style="lifeScrollThumbStyle"></div>
            </div>
            <button
              v-if="lifeIndexCanScroll"
              class="life-nav life-nav-prev"
              :disabled="lifeScrollLeft <= 0"
              @click="scrollLife(-1)"
              aria-label="prev"
            ></button>
            <button
              v-if="lifeIndexCanScroll"
              class="life-nav life-nav-next"
              :disabled="lifeMaxScroll > 0 && lifeScrollLeft >= lifeMaxScroll - 1"
              @click="scrollLife(1)"
              aria-label="next"
            ></button>
          </div>
        </div>

        <!-- 逐小时预报 -->
        <div class="card hourly-box" id="sec-hourly" ref="hourlySectionRef" v-if="status == 0 && hourly.length && compatFeatures.charts">
          <div class="card-header">
            <h2 class="card-title">{{ t('weather.hourlyForecast') }}</h2>
            <div class="hourly-sun-info" v-if="daily?.[0]">
              <div class="sun-group">
                <img :src="staticURL('/images/icon-sunrise.svg')" alt="sunrise" />
                <span>{{ formatSunTime(daily[0].sun_rise) }}</span>
              </div>
              <div class="sun-group">
                <img :src="staticURL('/images/icon-sunset.svg')" alt="sunset" />
                <span>{{ formatSunTime(daily[0].sun_down) }}</span>
              </div>
            </div>
          </div>
          <client-only>
            <div
              v-if="hourlyChartVisible && chartsEnabled"
              class="hourly-chart-mount"
              :class="{ 'is-sweep-in': hourlySweepIn }"
              :style="{ '--hourly-column-count': Math.max(hourly.length || 24, 1) }"
            >
              <div class="hourly-sweep-sentinel" aria-hidden="true" />
              <HourChart :realWeatherData="hourly" />
            </div>
            <IosUpgradeHint v-else-if="iosUpgradeRequired" compact />
            <p v-else-if="hourlyChartVisible" class="chart-fallback">{{ t('common.noData') }}</p>
          </client-only>
        </div>

        <!-- 15 天预报 -->
        <div class="card daily-box" id="sec-daily" ref="dailySectionRef" v-if="status == 0 && daily.length && compatFeatures.charts">
          <div class="card-header">
            <h2 class="card-title">{{ t('weather.dailyForecast15') }}</h2>
            <div class="chart-switch">
              <button
                class="switch-btn"
                :class="{ active: chartType === 'line' }"
                @click="chartType = 'line'"
              >
                {{ t('weather.lineChart') }}
              </button>
              <button
                class="switch-btn"
                :class="{ active: chartType === 'bar' }"
                @click="chartType = 'bar'"
              >
                {{ t('weather.barChart') }}
              </button>
            </div>
          </div>
          <client-only>
            <div
              v-if="dailyChartVisible && chartsEnabled"
              class="daily-chart-mount"
              :class="{ 'is-sweep-in': dailySweepIn }"
              :style="{ '--daily-day-count': Math.max(daily.length || 15, 1) }"
            >
              <div class="daily-sweep-sentinel" aria-hidden="true" />
              <DayChartLine
                v-show="chartType === 'line' && locale == 'zh-CN'"
                :forecastData="daily"
                :sync-scroll-left="dailyScrollLeft"
                :chart-active="chartType === 'line' && locale == 'zh-CN'"
                @scroll="onDailyChartScroll"
                @typography="onDailyChartTypography"
              />
              <EnDayChartLine
                v-show="chartType === 'line' && locale == 'en-US'"
                :forecastData="daily"
                :sync-scroll-left="dailyScrollLeft"
                :chart-active="chartType === 'line' && locale == 'en-US'"
                @scroll="onDailyChartScroll"
                @typography="onDailyChartTypography"
              />
              <DayChartBar
                v-show="chartType === 'bar' && locale == 'zh-CN'"
                :forecastData="daily"
                :sync-scroll-left="dailyScrollLeft"
                :chart-active="chartType === 'bar' && locale == 'zh-CN'"
                :typography-vars="dailyBarTypographyVars"
                @scroll="onDailyChartScroll"
              />
              <EnDayChartBar
                v-show="chartType === 'bar' && locale == 'en-US'"
                :forecastData="daily"
                :sync-scroll-left="dailyScrollLeft"
                :chart-active="chartType === 'bar' && locale == 'en-US'"
                :typography-vars="dailyBarTypographyVars"
                @scroll="onDailyChartScroll"
              />
            </div>
            <IosUpgradeHint v-else-if="iosUpgradeRequired" compact />
            <p v-else class="chart-fallback">{{ t('common.noData') }}</p>
          </client-only>
        </div>

        <!-- 天气详情 6 卡 -->
        <div class="card" id="sec-detail" v-if="status == 0 && compatFeatures.detailSection && weatherUiReady">
          <div class="card-header">
            <h2 class="card-title">{{ t('home.weatherDetail') }}</h2>
          </div>
          <div class="detail-grid">
            <!-- 风速（带罗盘） -->
            <div class="detail-card">
              <div class="d-top">
                <img :src="staticURL('/images/icon-wind.svg')" class="d-icon" alt="" />
                <span class="d-label">{{ t('home.windSpeed') }}</span>
              </div>
              <div class="d-value">
                {{ currentData?.wind_dir }} {{ currentData?.wind_level }}{{ t('weather.level') }}
              </div>
              <div class="d-bottom">
                <svg class="compass" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <!-- 背景圆环 -->
                  <path d="M50 3C75.9574 3 97 24.0426 97 50C97 75.9574 75.9574 97 50 97C24.0426 97 3 75.9574 3 50C3 24.0426 24.0426 3 50 3Z"
                    stroke="white" stroke-opacity="0.1" stroke-width="6"/>
                  <!-- 反向 1/4 弧（橙色，默认 Figma 弧中心 ~297°，跟随风向旋转） -->
                  <g :transform="`rotate(${windDegree - 117} 50 50)`">
                    <path d="M3.16699 49.9993C3.16699 34.3528 10.8126 20.4921 22.5716 11.9492"
                      stroke="#FF8700" stroke-width="6" stroke-linecap="round"/>
                  </g>
                  <!-- N/E/S/W（位于圈内对称位置） -->
                  <text x="50" y="18" text-anchor="middle" dominant-baseline="middle" font-size="9.6" fill="rgba(255,255,255,0.4)" font-family="PingFang SC">N</text>
                  <text x="82" y="50" text-anchor="middle" dominant-baseline="middle" font-size="9.6" fill="rgba(255,255,255,0.4)" font-family="PingFang SC">E</text>
                  <text x="50" y="82" text-anchor="middle" dominant-baseline="middle" font-size="9.6" fill="rgba(255,255,255,0.4)" font-family="PingFang SC">S</text>
                  <text x="18" y="50" text-anchor="middle" dominant-baseline="middle" font-size="9.6" fill="rgba(255,255,255,0.4)" font-family="PingFang SC">W</text>
                  <!-- 风向指针（Figma 原始 path，圆心对齐到 (50,50)，箭头尖端触及外圈） -->
                  <g :transform="`rotate(${windDegree - 123} 50 50)`">
                    <g transform="translate(50 50) scale(0.918) translate(-50 -50) translate(42 42)">
                      <path d="M0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8ZM51.166 35.5901L43.1911 20.2147L33.8631 34.8089L51.166 35.5901ZM8 8L7.19217 9.26389L38.9832 29.5835L39.791 28.3196L40.5989 27.0557L8.80783 6.73611L8 8Z"
                        fill="#FF8700"/>
                    </g>
                  </g>
                </svg>
              </div>
            </div>
            <!-- 湿度 -->
            <div class="detail-card">
              <div class="d-top">
                <img :src="staticURL('/images/icon-humidity.svg')" class="d-icon" alt="" />
                <span class="d-label">{{ t('weather.humidity') }}</span>
              </div>
              <div class="d-value">{{ currentData?.humidity ?? '--' }}%</div>
              <div class="d-bottom">
                <div class="humidity-bar">
                  <div class="humidity-fill" :style="{ width: `${currentData?.humidity || 0}%` }"></div>
                  <div class="humidity-dot" :style="{ left: `${currentData?.humidity || 0}%` }"></div>
                </div>
              </div>
            </div>
            <!-- 紫外线 -->
            <div class="detail-card">
              <div class="d-top">
                <img :src="staticURL('/images/icon-uv.svg')" class="d-icon" alt="" />
                <span class="d-label">{{ t('weather.uvIndex') }}</span>
              </div>
              <div class="d-value">
                {{ currentData?.uvi != null ? getStrengthLevel(currentData.uvi) : '--' }}
              </div>
              <div class="d-bottom">
                <img :src="staticURL('/images/icon-uv-gauge.svg')" class="d-gauge" alt="" />
              </div>
            </div>
            <!-- 气压 -->
            <div class="detail-card">
              <div class="d-top">
                <img :src="staticURL('/images/icon-pressure.svg')" class="d-icon" alt="" />
                <span class="d-label">{{ t('weather.airPressure') }}</span>
              </div>
              <div class="d-value">{{ currentData?.mslp ?? '--' }} hPa</div>
              <div class="d-bottom">
                <img :src="staticURL('/images/icon-pressure-chart.svg')" class="d-chart-img" alt="" />
              </div>
            </div>
            <!-- 空气质量（AQI） -->
            <div class="detail-card">
              <div class="d-top">
                <img :src="staticURL('/images/icon-air-quality.svg')" class="d-icon" alt="" />
                <span class="d-label">空气质量</span>
              </div>
              <div class="d-value">{{ aqiInfo?.text || '--' }} {{ aqiData?.aqi || '' }}</div>
              <div class="d-bottom">
                <svg class="aqi-gauge" viewBox="-6 0 142 96" fill="none" overflow="visible">
                  <!-- 背景半圆轨道 -->
                  <path d="M126.253 83.1265C126.253 50.9543 100.172 24.8735 68 24.8735C35.8278 24.8735 9.74708 50.9543 9.74708 83.1265"
                    stroke="rgba(255,255,255,0.1)" stroke-width="6" stroke-linecap="round"/>
                  <!-- 渐变进度弧 -->
                  <path :d="aqiArcPath" stroke="url(#aqi-gradient)" stroke-width="6" stroke-linecap="round"/>
                  <!-- 指示点 -->
                  <circle :cx="aqiDot.x" :cy="aqiDot.y" r="9.5" :fill="aqiDot.color" stroke="#fff" stroke-width="3"/>
                  <defs>
                    <linearGradient id="aqi-gradient" x1="0" y1="83" x2="0" y2="24" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stop-color="#16D22D"/>
                      <stop offset="20%" stop-color="#FFC300"/>
                      <stop offset="40%" stop-color="#FF8700"/>
                      <stop offset="60%" stop-color="#ED5A58"/>
                      <stop offset="80%" stop-color="#A053B0"/>
                      <stop offset="100%" stop-color="#A11D6E"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
            <!-- 能见度 -->
            <div class="detail-card">
              <div class="d-top">
                <img :src="staticURL('/images/icon-visibility.svg')" class="d-icon" alt="" />
                <span class="d-label">{{ t('weather.visibility') }}</span>
              </div>
              <div class="d-value">{{ visValue ?? '--' }}</div>
              <div class="d-bottom">
                <div class="vis-bars">
                  <div class="vis-bar" v-for="n in 5" :key="n" :style="{ width: `${20 + n * 20}px`, opacity: n * 0.2 }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 媒体服务 -->
        <div class="card" id="sec-media" v-if="compatFeatures.extras">
          <div class="card-header">
            <h2 class="card-title">{{ t('home.mediaService') }}</h2>
            <nuxt-link class="card-more" :to="localePath('/media')">
              <span>{{ t('weather.more') }}</span>
              <img :src="staticURL('/images/icon-more-arrow.svg')" alt="" />
            </nuxt-link>
          </div>
          <div class="media-grid">
            <div class="media-card" v-for="(m, i) in mediaList" :key="i">
              <div class="mc-icon">
                <img :src="m.icon" :alt="m.title" />
              </div>
              <div class="mc-text">
                <div class="mc-title">{{ m.title }}</div>
                <div class="mc-desc">{{ m.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 企业动态 -->
        <div class="card" id="sec-news" v-if="newslist.length && compatFeatures.extras">
          <div class="card-header">
            <h2 class="card-title">{{ t('home.enterpriseNews') }}</h2>
            <nuxt-link class="card-more" :to="localePath('/news')">
              <span>{{ t('weather.more') }}</span>
              <img :src="staticURL('/images/icon-more-arrow.svg')" alt="" />
            </nuxt-link>
          </div>
          <div class="news-list">
            <nuxt-link
              class="news-item"
              v-for="item in newslist"
              :key="item.id"
              :to="localePath('/news/' + item.id)"
            >
              <div class="news-date">
                <div class="day">{{ getDay(item.showTime) }}</div>
                <div class="ym">{{ getMonthYear(item.showTime) }}</div>
              </div>
              <div class="news-content">
                <div class="news-title">{{ item.title }}</div>
                <div class="news-desc">{{ item.subTitle }}</div>
              </div>
            </nuxt-link>
          </div>
        </div>
      </div>

      <!-- ============ 侧栏 ============ -->
      <aside class="home-side" v-if="compatFeatures.extras">
        <!-- 商业气象 -->
        <div class="side-card promo-card">
          <div class="promo-header">
            <span class="promo-title">{{ t('home.businessTitle') }}</span>
          </div>
          <div class="promo-divider"></div>
          <div class="promo-body">
            <div class="promo-desc">{{ t('home.businessDesc') }}</div>
            <div class="promo-stats-row" v-for="(row, ri) in promoStatsRows" :key="ri">
              <div class="promo-stat" v-for="(s, i) in row" :key="i">
                <div class="ps-num">{{ s.num }}</div>
                <div class="ps-lbl">{{ s.lbl }}</div>
              </div>
            </div>
          </div>
          <div class="promo-btn-wrap">
            <button type="button" class="pill-btn pill-btn-outline" @click="openBusinessSite">
              <span>了解更多</span>
              <img :src="staticURL('/images/icon-more-arrow.svg')" alt="" />
            </button>
          </div>
        </div>

        <!-- 行业解决方案 -->
        <div class="side-card promo-card promo-solution" v-if="solutionList.length">
          <div class="promo-header">
            <span class="promo-title">{{ t('home.solutionTitle') }}</span>
          </div>
          <div class="promo-divider"></div>
          <div
            class="solution-scroller"
            ref="solutionScrollerRef"
            @scroll="onSolutionScroll"
            @scrollend="onSolutionScrollEnd"
          >
            <a
              v-for="(s, i) in solutionScrollerItems"
              :key="i"
              :href="s.link || 'javascript:void(0)'"
              :target="s.link ? '_blank' : '_self'"
              class="solution-item"
              rel="noopener"
            >
              <div class="si-image">
                <img v-if="s.image" :src="s.image" :alt="s.title" />
              </div>
              <div class="si-content">
                <div class="si-title">{{ s.title }}</div>
                <div class="si-subtitle" v-if="s.subTitle">{{ s.subTitle }}</div>
              </div>
            </a>
          </div>
          <div class="solution-dots">
            <span
              v-for="(s, i) in solutionList"
              :key="i"
              class="dot"
              :class="{ active: i === solutionIndex }"
              @click="scrollToSolution(i)"
            ></span>
          </div>
        </div>

        <!-- 墨迹天气VIP + 联系我们 一起 sticky -->
        <div class="sticky-wrapper">
          <div class="side-card promo-card promo-vip">
            <div class="promo-header">
              <span class="promo-title">{{ t('home.vipTitle') }}</span>
            </div>
            <div class="promo-divider"></div>
            <div class="promo-body">
              <div class="promo-desc">{{ t('home.vipDesc') }}</div>
              <div class="vip-tags">
                <span class="vip-tag" v-for="tag in vipTags" :key="tag">{{ tag }}</span>
              </div>
            </div>
            <div class="promo-btn-wrap">
              <nuxt-link :to="localePath(locale == 'zh-CN' ? '/weather/VIP' : '/weather/MojiWeatherVIP')">
                <button class="pill-btn pill-btn-brand">
                  <span>查看详情</span>
                  <img :src="staticURL('/images/icon-more-arrow.svg')" alt="" />
                </button>
              </nuxt-link>
            </div>
          </div>

          <!-- 联系我们 -->
          <div class="side-card promo-card promo-contact">
            <div class="promo-header">
              <span class="promo-title">联系我们</span>
            </div>
            <div class="promo-divider"></div>
            <div class="contact-body">
              <div class="contact-item">
                <img :src="staticURL('/images/icon-email.svg')" alt="" />
                <span>business@moji.com</span>
              </div>
              <div class="contact-item">
                <img :src="staticURL('/images/icon-phone.svg')" alt="" />
                <span>400-888-6666</span>
              </div>
            </div>
            <div class="promo-btn-wrap">
              <button class="pill-btn pill-btn-outline" @click="popUpSwitch(true)">
                <span>在线咨询</span>
                <img :src="staticURL('/images/icon-more-arrow.svg')" alt="" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <error :status="status" v-if="status > 1 && !iosUpgradeOverlayVisible"></error>

    <IosUpgradeOverlay v-if="iosUpgradeOverlayVisible" />

    <!-- 城市/地区选择 -->
    <cityorarea
      v-model:cityVisible="cityVisible"
      ref="cityRef"
      @setCityId="setCityInfo"
    ></cityorarea>
    <!-- 生活服务弹窗（按需挂载，避免 aqiData 更新时在弹窗内触发非法 i18n 上下文） -->
    <service
      v-if="serviceVisible"
      v-model:serviceVisible="serviceVisible"
      :serviceItem="serviceItem"
      :aqi_forecast="aqi_forecast"
      :currentCity="currentCity"
    ></service>
    <!-- 空气质量弹窗 -->
    <aqi
      v-if="aqiVisible"
      :aqiData="aqiData"
      v-model:aqi-visible="aqiVisible"
      :currentCity="currentCity"
    ></aqi>
    <!-- 联系我们弹窗 -->
    <PopUp ref="contactPopUpRef" :title="$t('index.contactOnline')">
      <contactUsForm
        @success="popUpSwitch(false)"
        @submit="findBuryKey('home_page_Contact_button')"
        apiUrl="contactUs"
      />
    </PopUp>

    <CompatBootPanel v-if="compatFeatures.isActive && compatLevel != null" :level="compatLevel" />

    <!-- 调试面板（用于诊断折叠屏响应式问题） -->
    <div v-if="debugInfo.show" class="debug-panel" style="position:fixed;top:80px;right:16px;background:rgba(0,0,0,0.95);color:#fff;padding:16px;border-radius:8px;font-size:14px;z-index:99999;min-width:320px;box-shadow:0 4px 12px rgba(0,0,0,0.5);">
      <div style="font-weight:bold;margin-bottom:12px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid rgba(255,255,255,0.2);padding-bottom:8px;">
        <span>🔍 设备调试信息</span>
        <button @click="debugInfo.show = false" style="background:none;border:none;color:#fff;font-size:20px;cursor:pointer;padding:0;width:24px;height:24px;">×</button>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;line-height:1.6;">
        <span style="color:rgba(255,255,255,0.7);">视口宽度：</span>
        <span style="font-weight:500;">{{ debugInfo.width }}px</span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;line-height:1.6;">
        <span style="color:rgba(255,255,255,0.7);">pointer:coarse：</span>
        <span style="font-weight:500;" :style="{color: debugInfo.isCoarse ? '#4ade80' : '#f87171'}">
          {{ debugInfo.isCoarse ? '✓ 是（触摸）' : '✗ 否（鼠标）' }}
        </span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;line-height:1.6;">
        <span style="color:rgba(255,255,255,0.7);">pointer:fine：</span>
        <span style="font-weight:500;" :style="{color: debugInfo.isFine ? '#4ade80' : '#f87171'}">
          {{ debugInfo.isFine ? '✓ 是（鼠标）' : '✗ 否（触摸）' }}
        </span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;line-height:1.6;">
        <span style="color:rgba(255,255,255,0.7);">响应式断点：</span>
        <span style="font-weight:500;">{{ debugInfo.breakpoint }}</span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;line-height:1.6;">
        <span style="color:rgba(255,255,255,0.7);">应用布局：</span>
        <span style="font-weight:500;" :style="{color: debugInfo.layout.includes('Responsive') ? '#4ade80' : '#fbbf24'}">
          {{ debugInfo.layout }}
        </span>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:8px;line-height:1.6;">
        <span style="color:rgba(255,255,255,0.7);">媒体查询匹配：</span>
        <span style="font-weight:500;" :style="{color: debugInfo.responsiveMatch ? '#4ade80' : '#f87171'}">
          {{ debugInfo.responsiveMatch ? '✓ 响应式生效' : '✗ 固定布局' }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/css/home.scss';
</style>

<style lang="scss">
// 整页横划规则
// 桌面浏览器 500-767px：锁定文档流 ≥768
@media (pointer: fine) and (min-width: 500px) and (max-width: 767px) {
  body.home-body { min-width: 768px; }
}

// iPad 等平板 768-1200px：锁定文档流 ≥768
@media ((hover: none) or (pointer: coarse) or (any-pointer: coarse)) and (min-width: 768px) and (max-width: 1200px) {
  body.home-body { min-width: 768px; }
}

.compat-shell-banner {
  margin: 16px 16px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.2);
  color: #bfdbfe;
  font-size: 13px;
  line-height: 1.5;
}

// 调试面板样式
.debug-panel {
  position: fixed;
  top: 80px;
  right: 16px;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 9999;
  min-width: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);

  .debug-title {
    font-weight: bold;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding-bottom: 8px;

    button {
      background: none;
      border: none;
      color: #fff;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
      }
    }
  }

  .debug-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    line-height: 1.6;

    .debug-label {
      color: rgba(255, 255, 255, 0.7);
      margin-right: 8px;
    }

    .debug-value {
      font-weight: 500;
      text-align: right;

      &.yes {
        color: #4ade80;
      }

      &.no {
        color: #f87171;
      }
    }
  }
}
</style>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick, defineAsyncComponent } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'
import { findBuryKey, sendBuryClient, useLocalePath, useRuntimeConfig } from '#imports'
import {
  getDay,
  getMonthYear,
  getAQIInfo
} from '@/utils/utils'
import useServerRequest from '@/composables/useServerRequest'
import { shouldShowDeviceDebugPanel } from '@/utils/debugPanel'
import { iosSupportsCharts, iosSupportsRadarIframe, isIosUpgradeRequired } from '@/utils/iosCompat'
import { getCompatFeatures, parseCompatLevel, resolveCompatLevel } from '@/utils/compatMode'
import { getBrowserLngLat } from '@/utils/location'
import {
  safeStorageGetJSON,
  safeStorageSetJSON,
} from '@/utils/safeLocalStorage'
import { filterLifeIndexList, getLifeIndexIconId } from '@/utils/lifeIndex'
import type {
  WeatherParams,
  NewsListParams,
  GetNewsListResponse,
  configParams,
  configResponse,
  WeatherIndex,
  WeatherResponse
} from '~/types'
import type { DaychartTypographyVars } from '~/utils/daychartTypography'
import { useRouter,useRoute } from 'vue-router'

const { locale, t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const config = useRuntimeConfig()
const nuxtApp = useNuxtApp()
const cmsurlStrategy = config?.public?.cmsStrategy

const compatLevel = computed(() => {
  const fromQuery = parseCompatLevel(route.query.compat as string)
  if (fromQuery != null) return fromQuery
  if (import.meta.client) return resolveCompatLevel()
  return null
})
const compatFeatures = computed(() => getCompatFeatures(compatLevel.value))
const chartsEnabled = computed(() => compatFeatures.value.charts && iosSupportsCharts())
const radarIframeEnabled = computed(() => compatFeatures.value.radarIframe && iosSupportsRadarIframe())
const iosUpgradeRequired = computed(() => import.meta.client && isIosUpgradeRequired())
/** iOS 13 且加载失败：全屏升级提示 + 锁定滚动 */
const iosUpgradeOverlayVisible = computed(
  () => iosUpgradeRequired.value && status.value === 2,
)

// 图表懒加载（拆包 echarts，进入视口再挂载）
const HourChart = defineAsyncComponent(() => import('@/components/hourchart.vue'))
const DayChartLine = defineAsyncComponent(() => import('@/components/daychartline.vue'))
const EnDayChartLine = defineAsyncComponent(() => import('@/components/endaychartline.vue'))
const DayChartBar = defineAsyncComponent(() => import('@/components/daychartbar.vue'))
const EnDayChartBar = defineAsyncComponent(() => import('@/components/endaychartbar.vue'))

// ===== 状态 =====
const status = ref<number>(-1)
const weatherReady = ref(false)
/** 天气写入后 nextTick 再渲染含 AQI 区块，避免 iPad legacy 下同 tick 渲染抛错 */
const weatherUiReady = ref(false)
/** 首屏天气已成功展示后，切换城市时保持页面挂载，避免整页闪白 */
const hasLoadedWeatherOnce = ref(false)
/** 有天气数据、暂无数据、加载中或失败时展示主内容区；L3 强制展示骨架 */
const homeContentVisible = computed(() => {
  if (compatFeatures.value.forceHomeShell) return true
  if (status.value === -1 && import.meta.client) return true
  if (hasLoadedWeatherOnce.value) return true
  return (weatherReady.value && status.value === 0) || status.value === 1 || status.value === 2
})
const chartType = ref<'line' | 'bar'>('line')
const dailyScrollLeft = ref(0)
const onDailyChartScroll = (left: number) => {
  if (Math.round(dailyScrollLeft.value) !== Math.round(left)) {
    dailyScrollLeft.value = left
  }
}
const dailyBarTypographyVars = ref<DaychartTypographyVars>({})
const onDailyChartTypography = (vars: DaychartTypographyVars) => {
  dailyBarTypographyVars.value = vars
}
const cityVisible = ref<boolean>(false)
const cityRef = ref<any>(null)
const aqiVisible = ref<boolean>(false)
const serviceVisible = ref<boolean>(false)
const serviceItem = ref<WeatherIndex>()

// ===== 调试信息（仅本机 / 局域网 dev 展示，见 utils/debugPanel.ts）=====
const debugPanelEnabled = ref(false)
const debugInfo = reactive({
  show: false,
  width: 0,
  isCoarse: false,
  isFine: false,
  breakpoint: '',
  layout: '',
  responsiveMatch: false
})

const isTouchLikeDevice = () => {
  if (typeof window === 'undefined') return false
  const hasTouchPointer = window.matchMedia('(hover: none), (pointer: coarse), (any-pointer: coarse)').matches
  const maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0
  return hasTouchPointer || maxTouchPoints > 1
}

const syncIpadLayoutClass = () => {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return
  const ua = navigator.userAgent || ''
  const maxTouchPoints = navigator.maxTouchPoints || 0
  const isIpadUa = /iPad/i.test(ua)
  const isTouchMac = /Macintosh|Mac OS X/i.test(ua) && maxTouchPoints > 1
  document.documentElement.classList.toggle('ios-ipad', isIpadUa || isTouchMac)
}

const bindDebugPanel = () => {
  debugPanelEnabled.value = shouldShowDeviceDebugPanel()
  debugInfo.show = debugPanelEnabled.value
  if (!debugPanelEnabled.value) return
  updateDebugInfo()
  window.addEventListener('resize', updateDebugInfo)
}

const unbindDebugPanel = () => {
  if (!debugPanelEnabled.value) return
  window.removeEventListener('resize', updateDebugInfo)
}

const updateDebugInfo = () => {
  if (typeof window === 'undefined') return

  const width = window.innerWidth
  const isCoarse = isTouchLikeDevice()
  const isFine = window.matchMedia('(pointer: fine)').matches

  let breakpoint = ''
  let layout = ''
  let responsiveMatch = false

  if (width < 500) {
    breakpoint = '< 500px'
    layout = 'Mobile'
    responsiveMatch = true
  } else if (isCoarse && width >= 500 && width <= 1200) {
    breakpoint = '500-1200px (coarse)'
    layout = 'Foldable Responsive'
    responsiveMatch = true
  } else if (width >= 500 && width <= 911) {
    breakpoint = '500-911px'
    layout = 'Pad'
    responsiveMatch = false
  } else if (width >= 912 && width <= 1335) {
    breakpoint = '912-1335px'
    layout = 'Small Desktop'
    responsiveMatch = false
  } else {
    breakpoint = '> 1335px'
    layout = 'Desktop'
    responsiveMatch = false
  }

  debugInfo.width = width
  debugInfo.isCoarse = isCoarse
  debugInfo.isFine = isFine
  debugInfo.breakpoint = breakpoint
  debugInfo.layout = layout
  debugInfo.responsiveMatch = responsiveMatch
}

const currentCity = ref<any>({})
/** 移动端 hero 左右 padding + 定位/箭头占位（px） */
const HERO_CITY_PAGE_INSET = 64
const HERO_CITY_CHROME = 60
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 375)
const updateViewportWidth = () => {
  if (typeof window === 'undefined') return
  viewportWidth.value = window.innerWidth
}
const heroCityNameLimits = computed(() => {
  const width = viewportWidth.value
  const isMobile = width <= 499.98
  const charWidth = isMobile ? 19 : 24
  const maxChars = Math.max(
    4,
    Math.floor((width - HERO_CITY_PAGE_INSET - HERO_CITY_CHROME) / charWidth),
  )
  if (width <= 375) {
    const limit = Math.min(13, maxChars)
    return { maxLen: limit, truncateLen: limit }
  }
  if (isMobile) {
    const limit = Math.min(15, maxChars)
    return { maxLen: limit, truncateLen: Math.max(limit - 1, 4) }
  }
  return { maxLen: 15, truncateLen: 14 }
})
const truncateHeroCityName = (name: string | undefined | null) => {
  const text = (name ?? '').trim()
  const { maxLen, truncateLen } = heroCityNameLimits.value
  if (text.length <= maxLen) return text
  return `${text.slice(0, truncateLen)}...`
}
const heroCityDisplayName = computed(() =>
  truncateHeroCityName(locale.value === 'zh-CN' ? currentCity.value?.name : currentCity.value?.enname),
)

// Hero 天气描述：平板段 90px 宽 @ 24px 约 3 字单行，超出则 18px 换行
const HERO_WEATHER_TEXT_SINGLE_LINE_MAX = 3

const isHeroWeatherTextAdaptiveLayout = computed(() => {
  const w = viewportWidth.value
  if (isTouchLikeDevice()) return w >= 768 && w <= 1200
  return w >= 500 && w <= 911
})

const currentData = ref<WeatherResponse['current']>()

const heroWeatherTextWrap = computed(() => {
  if (!weatherUiReady.value || !isHeroWeatherTextAdaptiveLayout.value) return false
  const text = (currentData.value?.weather ?? '').trim()
  return text.length > HERO_WEATHER_TEXT_SINGLE_LINE_MAX
})

const aqiData = ref<WeatherResponse['aqi']>()
const aqi_forecast = ref<WeatherResponse['aqi_forecast']>([])
const aqi_forecast_hourly = ref<WeatherResponse['aqi_forecast']>([])
const daily = ref<WeatherResponse['daily']>([])
const hourly = ref<WeatherResponse['hourly']>([])
const indexData = ref<WeatherResponse['index']>([])
const newslist = ref<GetNewsListResponse['list']>([])

// ===== 天气预警 =====
const alertList = ref<NonNullable<WeatherResponse['alerts']>>([])
const alertIndex = ref(0)
const alertTrackResetting = ref(false)
let alertTimer: ReturnType<typeof setInterval> | null = null
const ALERT_ITEM_HEIGHT = 22
const ALERT_ROTATE_INTERVAL = 4000
const alertTrackItems = computed(() => {
  const list = alertList.value
  if (list.length <= 1) return list
  return [...list, list[0]]
})
const currentAlert = computed(() => {
  const list = alertList.value
  if (!list.length) return undefined
  return list[alertIndex.value % list.length]
})
const ALERT_LEVEL_KEY: Record<string, string> = {
  白色: 'white', 蓝色: 'blue', 黄色: 'yellow', 橙色: 'orange', 红色: 'red',
  White: 'white', Blue: 'blue', Yellow: 'yellow', Orange: 'orange', Red: 'red'
}
const alertLevelKey = (level?: string) => (level && ALERT_LEVEL_KEY[level]) || 'orange'
const formatAlert = (a?: { alert_name?: string; alert_level?: string }) => {
  if (!a) return ''
  const name = a.alert_name || ''
  const level = a.alert_level || ''
  return `${name}${level}${t('weather.alertSuffix')}`
}
/** 避免模板内 optional chaining 链在旧 iOS 编译/运行边界出问题 */
const formatSunTime = (raw?: string) => {
  if (!raw) return ''
  const parts = raw.split(' ')
  const time = parts[parts.length - 1] || ''
  return time.slice(0, 5)
}
const stopAlertRotate = () => {
  if (alertTimer) {
    clearInterval(alertTimer)
    alertTimer = null
  }
}
const onAlertTrackTransitionEnd = (e: TransitionEvent) => {
  if (e.propertyName !== 'transform') return
  const len = alertList.value.length
  if (len <= 1 || alertIndex.value < len) return
  alertTrackResetting.value = true
  alertIndex.value = 0
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      alertTrackResetting.value = false
    })
  })
}
const startAlertRotate = () => {
  stopAlertRotate()
  alertTrackResetting.value = false
  if (alertList.value.length <= 1) return
  alertTimer = setInterval(() => {
    const len = alertList.value.length
    if (alertIndex.value >= len) return
    alertIndex.value++
  }, ALERT_ROTATE_INTERVAL)
}
onBeforeUnmount(() => stopAlertRotate())
onBeforeUnmount(() => stopSolutionRotate())
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') window.removeEventListener('resize', updateLifeIndexLayout)
})

const DEFAULT_CITY_ID = 2
let userPickedCity = false

const weatherParams = reactive<WeatherParams>({
  lat: '',
  lon: '',
  cityId: undefined as any
})

// ===== 天气卡片 iframe 坐标（来自 getCityData 接口的 city 对象）=====
const cityLon = ref('')  // city.longtitude
const cityLat = ref('')  // city.latitude
/** 自动定位请求城市数据期间，先不要用接口返回的城市中心点初始化雷达 */
let awaitingDeviceRadarLocation = false

const RADAR_MAP_BASE = 'https://html5.moji.com/tpd/weathermap/index.html'
const RADAR_IFRAME_ORIGIN = 'https://html5.moji.com'
/** 首次 URL 带定位坐标，后续换城市保持 iframe 实例并通过消息触发地图滑动 */
const radarSrc = ref('')
let initialRadarLngLat: { lng: number; lat: number } | null = null
const initRadarSrc = () => {
  if (radarSrc.value || awaitingDeviceRadarLocation) return
  const rawLon = String(cityLon.value ?? '').trim()
  const rawLat = String(cityLat.value ?? '').trim()
  if (!rawLon || !rawLat) return
  const lon = Number(rawLon)
  const lat = Number(rawLat)
  if (
    !Number.isFinite(lon) ||
    !Number.isFinite(lat) ||
    lon < -180 ||
    lon > 180 ||
    lat < -90 ||
    lat > 90
  ) return
  initialRadarLngLat = { lng: lon, lat }
  radarSrc.value = `${RADAR_MAP_BASE}#/index?showType=small&lon=${encodeURIComponent(lon)}&lat=${encodeURIComponent(lat)}`
}

// ===== 雷达 iframe / 图表：进入视口再挂载（减小首屏 JS 与 echarts 开销）=====
const radarBoxRef = ref<HTMLElement | null>(null)
const radarIframeRef = ref<HTMLIFrameElement | null>(null)
const radarVisible = ref(false)
/** iframe 未 load 前保持透明，露出 radar-box 暗色底，避免刷新白闪 */
const radarIframeLoaded = ref(false)

const postRadarLngLat = () => {
  const lng = Number(String(cityLon.value ?? '').trim())
  const lat = Number(String(cityLat.value ?? '').trim())
  if (
    !Number.isFinite(lng) ||
    !Number.isFinite(lat) ||
    lng < -180 ||
    lng > 180 ||
    lat < -90 ||
    lat > 90
  ) return
  radarIframeRef.value?.contentWindow?.postMessage(
    { type: 'updateLngLat', lng, lat },
    RADAR_IFRAME_ORIGIN,
  )
}

const onRadarIframeLoad = () => {
  radarIframeLoaded.value = true
  console.log('[home] iframe loaded, src=', radarSrc.value)
  const lng = Number(String(cityLon.value ?? '').trim())
  const lat = Number(String(cityLat.value ?? '').trim())
  if (
    initialRadarLngLat &&
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    (lng !== initialRadarLngLat.lng || lat !== initialRadarLngLat.lat)
  ) {
    postRadarLngLat()
  }
}

// 保持 iframe 不重建，切换城市时由 H5 执行 panTo 滑动
watch([cityLon, cityLat], () => {
  initRadarSrc()
  if (radarIframeLoaded.value) postRadarLngLat()
})
const hourlySectionRef = ref<HTMLElement | null>(null)
const hourlyChartVisible = ref(false)
const hourlySweepIn = ref(false)
let hourlySweepObserver: IntersectionObserver | null = null
let hourlySweepRetry = 0
let hourlySweepStarted = false
const dailySectionRef = ref<HTMLElement | null>(null)
const dailyChartVisible = ref(false)
const dailySweepIn = ref(false)
let dailySweepObserver: IntersectionObserver | null = null
let dailySweepRetry = 0
let dailySweepStarted = false

const stopDailySweepObserver = () => {
  dailySweepObserver?.disconnect()
  dailySweepObserver = null
}

const stopHourlySweepObserver = () => {
  hourlySweepObserver?.disconnect()
  hourlySweepObserver = null
}

const revealHourlySweep = () => {
  if (hourlySweepStarted || hourlySweepIn.value) return
  hourlySweepStarted = true
  stopHourlySweepObserver()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      hourlySweepIn.value = true
    })
  })
}

const startHourlySweepObserver = () => {
  if (hourlySweepStarted || hourlySweepIn.value || hourlySweepObserver) return
  const section = hourlySectionRef.value
  if (!section || !hourlyChartVisible.value) return

  if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
    revealHourlySweep()
    return
  }

  const sentinel = section.querySelector('.hourly-sweep-sentinel') as HTMLElement | null
  if (!sentinel) {
    if (hourlySweepRetry < 60) {
      hourlySweepRetry += 1
      requestAnimationFrame(startHourlySweepObserver)
    } else {
      revealHourlySweep()
    }
    return
  }

  hourlySweepObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
        revealHourlySweep()
      }
    },
    { threshold: 0.4, rootMargin: '0px 0px -12% 0px' },
  )
  hourlySweepObserver.observe(sentinel)
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const revealDailySweep = () => {
  if (dailySweepStarted || dailySweepIn.value) return
  dailySweepStarted = true
  stopDailySweepObserver()
  // 先让「全隐」这一帧画出来，下一帧再加 class，避免刷新时动画被跳过
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      dailySweepIn.value = true
    })
  })
}

const startDailySweepObserver = () => {
  if (dailySweepStarted || dailySweepIn.value || dailySweepObserver) return
  const section = dailySectionRef.value
  if (!section || !dailyChartVisible.value) return

  if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
    revealDailySweep()
    return
  }

  const sentinel = section.querySelector('.daily-sweep-sentinel') as HTMLElement | null
  if (!sentinel) {
    if (dailySweepRetry < 60) {
      dailySweepRetry += 1
      requestAnimationFrame(startDailySweepObserver)
    } else {
      revealDailySweep()
    }
    return
  }

  dailySweepObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
        revealDailySweep()
      }
    },
    // 日期行大约在图表顶部 40px；底部负边距避免模块刚擦到屏幕底边就把动画播完
    { threshold: 0.4, rootMargin: '0px 0px -12% 0px' },
  )
  dailySweepObserver.observe(sentinel)
}

watch(
  [dailyChartVisible, dailySectionRef],
  () => {
    nextTick(() => {
      requestAnimationFrame(startDailySweepObserver)
    })
  },
  { flush: 'post' },
)

watch(
  [hourlyChartVisible, hourlySectionRef],
  () => {
    nextTick(() => {
      requestAnimationFrame(startHourlySweepObserver)
    })
  },
  { flush: 'post' },
)

const observeOnceVisible = (el: HTMLElement | null, onVisible: () => void, rootMargin = '200px') => {
  if (!el || typeof IntersectionObserver === 'undefined') {
    onVisible()
    return
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        onVisible()
        io.disconnect()
      }
    },
    { rootMargin }
  )
  io.observe(el)
}

onMounted(() => {
  bindDebugPanel()
  updateViewportWidth()

  updateLifeIndexLayout()
  window.addEventListener('resize', updateLifeIndexLayout)
  window.addEventListener('resize', updateViewportWidth)
  window.addEventListener('resize', updateHotCityScroll)

  observeOnceVisible(radarBoxRef.value, () => { radarVisible.value = true })
  observeOnceVisible(hourlySectionRef.value, () => { hourlyChartVisible.value = true })
  observeOnceVisible(dailySectionRef.value, () => { dailyChartVisible.value = true })
})

onBeforeUnmount(() => {
  unbindDebugPanel()
  window.removeEventListener('resize', updateLifeIndexLayout)
  window.removeEventListener('resize', updateViewportWidth)
  window.removeEventListener('resize', updateHotCityScroll)
  stopDailySweepObserver()
  stopHourlySweepObserver()
})

// ===== 计算属性 =====
const aqiInfo = computed(() => {
  if (!aqiData.value?.aqi) return null
  return getAQIInfo(+aqiData.value.aqi, nuxtApp as any)
})

// 左侧浮动导航
const activeSection = ref('sec-current')
const navItems = [
  { key: 'sec-current', label: '实况天气', icon: staticURL('/images/nav-current.svg') },
  { key: 'sec-life', label: '生活指数', icon: staticURL('/images/nav-life.svg') },
  { key: 'sec-hourly', label: '逐小时', icon: staticURL('/images/nav-hourly.svg') },
  { key: 'sec-daily', label: '15天', icon: staticURL('/images/nav-daily.svg') },
  { key: 'sec-detail', label: '天气详情', icon: staticURL('/images/nav-detail.svg') },
  { key: 'sec-media', label: '媒体服务', icon: staticURL('/images/nav-media.svg') },
  { key: 'sec-news', label: '企业动态', icon: staticURL('/images/nav-news.svg') }
]
const scrollToSection = (id: string) => {
  const el = document.getElementById(id)
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY - 100
  window.scrollTo({ top, behavior: 'smooth' })
}
const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })
const refreshPage = () => {
  if (typeof window !== 'undefined') window.location.reload()
}
const BACK_TOP_THRESHOLD = 120
const showBackTopBtn = ref(false)
const onPageScroll = () => {
  const scrollY = window.scrollY || document.documentElement.scrollTop
  showBackTopBtn.value = scrollY > BACK_TOP_THRESHOLD

  const offset = 200
  for (let i = navItems.length - 1; i >= 0; i--) {
    const el = document.getElementById(navItems[i].key)
    if (el && el.getBoundingClientRect().top <= offset) {
      activeSection.value = navItems[i].key
      return
    }
  }
  activeSection.value = navItems[0].key
}

// 风向角度：根据 wind_dir 文本派生（API 不返回 wind_degrees）
const WIND_DIR_MAP: Record<string, number> = {
  '北': 0, '北风': 0,
  '东北': 45, '东北风': 45,
  '东': 90, '东风': 90,
  '东南': 135, '东南风': 135,
  '南': 180, '南风': 180,
  '西南': 225, '西南风': 225,
  '西': 270, '西风': 270,
  '西北': 315, '西北风': 315,
  '无持续风向': 0
}
const windDegree = computed(() => {
  const dir = (currentData.value as any)?.wind_dir
  return WIND_DIR_MAP[dir] ?? 0
})

// AQI 仪表盘：根据 0-500 数值在半圆弧上定位指示点
// 等级：优0-50 良51-100 轻度101-150 中度151-200 重度201-300 严重301-500
const AQI_COLORS = [
  { max: 50, color: '#16D22D' },
  { max: 100, color: '#FFC300' },
  { max: 150, color: '#FF8700' },
  { max: 200, color: '#ED5A58' },
  { max: 300, color: '#A053B0' },
  { max: 500, color: '#A11D6E' }
]
const getAqiColor = (aqi: number) => {
  for (const lv of AQI_COLORS) if (aqi <= lv.max) return lv.color
  return AQI_COLORS[AQI_COLORS.length - 1].color
}
// 半圆参数（与 SVG path 一致）：圆心 (68,83)，半径 ~58，从角度 180° 到 360°
const AQI_CX = 68
const AQI_CY = 83
const AQI_R = 58.25
const aqiPct = computed(() => {
  const v = Number(aqiData.value?.aqi || 0)
  return Math.max(0, Math.min(1, v / 500))
})
const aqiDot = computed(() => {
  // 角度从 180°（左端）到 360°（右端），逆时针方向（弧顶在 0°/180°的中间，即顶部）
  const angle = Math.PI + aqiPct.value * Math.PI
  return {
    x: AQI_CX + AQI_R * Math.cos(angle),
    y: AQI_CY + AQI_R * Math.sin(angle),
    color: getAqiColor(Number(aqiData.value?.aqi || 0))
  }
})
const aqiArcPath = computed(() => {
  // SVG arc from start point (左端) 沿弧到当前 dot 位置
  const startX = AQI_CX + AQI_R * Math.cos(Math.PI)
  const startY = AQI_CY + AQI_R * Math.sin(Math.PI)
  const endX = aqiDot.value.x
  const endY = aqiDot.value.y
  const largeArc = 0
  const sweep = 1
  return `M${startX} ${startY} A${AQI_R} ${AQI_R} 0 ${largeArc} ${sweep} ${endX} ${endY}`
})

// 能见度：current 无该字段，降级取 hourly[0].vis
const visValue = computed(() => {
  const v = (currentData.value as any)?.vis ?? hourly.value?.[0]?.vis
  if (v == null) return null
  if (v < 1000) return '小于1km'
  return `${Math.round(v / 1000)} km`
})

// 生活指数（国内用 index_type_id 白名单；海外 currentIndex 为 1–8 序号 id 时展示全部）
const lifeIndexList = computed(() => filterLifeIndexList(indexData.value || []))

const lifeIndexLayout = computed(() => {
  const list = lifeIndexList.value
  const cols = lifeIndexCellCount.value
  const canScroll = list.length > cols

  if (!canScroll) {
    return {
      topRow: list,
      bottomRow: [] as typeof list,
      topPad: 0,
      bottomPad: 0,
      rowLen: list.length
    }
  }

  const topRow = list.slice(0, Math.ceil(list.length / 2))
  const bottomRow = list.slice(Math.ceil(list.length / 2))
  const rowLen = Math.max(topRow.length, bottomRow.length)
  return {
    topRow,
    bottomRow,
    topPad: rowLen - topRow.length,
    bottomPad: rowLen - bottomRow.length,
    rowLen
  }
})

// 生活指数横向滑动：分档 ≥912 桌面 5 列；500–911 平板 4 列；<500 移动端 2 列
const lifeViewportEl = ref<HTMLElement | null>(null)
const lifeIndexViewportWidth = ref(771) // Figma 310:7027 Desktop 每行可视宽
const lifeIndexCellCount = ref(5)

const LIFE_CELL_GAP = 1
// Figma 310:7027 Desktop & SmallDesktop：与 SCSS $life-desktop-* 保持一致
const LIFE_DESKTOP_VIEWPORT_W = 771
const LIFE_DESKTOP_CELL_W = 153.4
// Figma 108:17350 Pad 768：与 SCSS $life-pad-* 保持一致
const LIFE_PAD_VIEWPORT_W = 580
const LIFE_PAD_CELL_W = 144.25

const getLifeCellWidth = () => {
  const cols = lifeIndexCellCount.value
  if (cols <= 0) return 0
  if (cols === 5) return LIFE_DESKTOP_CELL_W
  if (cols === 4) return LIFE_PAD_CELL_W
  if (cols === 2) {
    return (lifeIndexViewportWidth.value - LIFE_CELL_GAP) / 2
  }
  const gap = (cols - 1) * LIFE_CELL_GAP
  return Math.floor((lifeIndexViewportWidth.value - gap) / cols)
}

const getLifeCellStep = () => getLifeCellWidth() + LIFE_CELL_GAP

const updateLifeIndexLayout = () => {
  if (typeof window === 'undefined') return
  const w = window.innerWidth
  if (w < 500) {
    lifeIndexCellCount.value = 2 // 移动端每行 2 个，2×2 横划布局
  } else if (isTouchLikeDevice() ? w <= 1200 : w <= 911) {
    lifeIndexCellCount.value = 4
  } else {
    lifeIndexCellCount.value = 5
  }
  updateMobileIndicators()
  nextTick(() => {
    requestAnimationFrame(() => {
      if (!lifeViewportEl.value) return
      const el = lifeViewportEl.value
      const isPadLayout = w >= 500 && (isTouchLikeDevice() ? w <= 1200 : w <= 911)
      const isDesktopLayout = !isPadLayout && w > 911
      const contentW = isPadLayout
        ? LIFE_PAD_VIEWPORT_W
        : isDesktopLayout
          ? LIFE_DESKTOP_VIEWPORT_W
          : el.clientWidth
      if (contentW > 0) {
        lifeIndexViewportWidth.value = contentW
        const cellW = getLifeCellWidth()
        if (cellW > 0) {
          el.style.setProperty('--life-cell-w', `${cellW}px`)
        }
        const max = lifeMaxScroll.value
        if (el.scrollLeft > max) el.scrollLeft = max
        lifeScrollLeft.value = el.scrollLeft
      }
    })
  })
}
const lifeScrollLeft = ref(0)
const lifeScrolling = ref(false)
let lifeScrollRaf = 0
let lifeScrollEndTimer = 0

const markLifeScrolling = () => {
  lifeScrolling.value = true
  activeLifeId.value = null
  if (lifeScrollEndTimer) window.clearTimeout(lifeScrollEndTimer)
  lifeScrollEndTimer = window.setTimeout(() => {
    lifeScrollEndTimer = 0
    lifeScrolling.value = false
    lifeDragMoved = false
  }, 180)
}

const onLifeScroll = () => {
  markLifeScrolling()
  if (lifeScrollRaf) return
  lifeScrollRaf = requestAnimationFrame(() => {
    lifeScrollRaf = 0
    const el = lifeViewportEl.value
    if (!el) return
    lifeScrollLeft.value = el.scrollLeft
  })
}

// 两行长度不一致时，用占位 cell 把短行补齐（仅可横划时）
const lifeRowLen = computed(() => lifeIndexLayout.value.rowLen)

const lifeIndexCanScroll = computed(() => lifeIndexList.value.length > lifeIndexCellCount.value)
const lifeMaxScroll = computed(() => {
  const overflow = Math.max(0, lifeRowLen.value - lifeIndexCellCount.value)
  return overflow * getLifeCellStep()
})

// 是否显示移动端指示器（响应式）
const showMobileIndicators = ref(false)
const updateMobileIndicators = () => {
  if (typeof window !== 'undefined') {
    showMobileIndicators.value = window.innerWidth < 500
  }
}

// 滚动条样式（与逐小时/15 天一致：百分比 left/width，避免 iOS 上 transform+长 transition 抖动）
const lifeScrollThumbStyle = computed(() => {
  if (!lifeIndexCanScroll.value || lifeMaxScroll.value <= 0) {
    return { width: '100%', left: '0' }
  }

  const cellW = getLifeCellWidth()
  const totalWidth = lifeRowLen.value * cellW + Math.max(0, lifeRowLen.value - 1) * LIFE_CELL_GAP
  const visibleWidth = lifeIndexCellCount.value * cellW + Math.max(0, lifeIndexCellCount.value - 1) * LIFE_CELL_GAP
  const ratio = totalWidth > 0 ? Math.min(1, visibleWidth / totalWidth) : 1
  if (ratio >= 1) return { width: '100%', left: '0' }

  const scrollRatio = Math.min(1, Math.max(0, lifeScrollLeft.value / lifeMaxScroll.value))
  const maxLeftPct = (1 - ratio) * 100

  return {
    width: `${ratio * 100}%`,
    left: `${scrollRatio * maxLeftPct}%`
  }
})

const scrollLife = (dir: number) => {
  const el = lifeViewportEl.value
  if (!el) return
  const step = getLifeCellStep() * lifeIndexCellCount.value // 一次滑一整屏
  const next = el.scrollLeft + dir * step
  el.scrollTo({
    left: Math.max(0, Math.min(lifeMaxScroll.value, next)),
    behavior: 'smooth'
  })
}

// 区分横滑与点击，避免拖动后误开弹窗（滚动由原生 overflow 处理，与 15 天/逐小时一致）
let lifeTouchStartX = 0
let lifeTouchStartY = 0
let lifeScrollDirection = null as 'horizontal' | 'vertical' | null
let lifeDragMoved = false

// 统一从 Touch / Mouse 事件取坐标，使桌面浏览器（含 DevTools 设备模式鼠标拖拽）与真机一致
const getPointer = (e: TouchEvent | MouseEvent) => {
  if ('touches' in e) {
    const t = e.touches[0] || e.changedTouches[0]
    return { x: t?.clientX ?? 0, y: t?.clientY ?? 0 }
  }
  return { x: e.clientX, y: e.clientY }
}

const handleLifeTouchStart = (e: TouchEvent | MouseEvent) => {
  const p = getPointer(e)
  lifeTouchStartX = p.x
  lifeTouchStartY = p.y
  lifeScrollDirection = null
  lifeDragMoved = false
}

const handleLifeTouchMove = (e: TouchEvent | MouseEvent) => {
  const p = getPointer(e)
  const deltaX = p.x - lifeTouchStartX
  const deltaY = p.y - lifeTouchStartY
  if (lifeScrollDirection === null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
    lifeScrollDirection = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
  }
  if (lifeScrollDirection === 'horizontal') {
    lifeDragMoved = true
    markLifeScrolling()
  }
}

const handleLifeTouchEnd = () => {
  lifeScrollDirection = null
}

// 仅用于区分横滑与点击；横向滚动交给原生 overflow（与 15 天/逐小时一致）
// 桌面鼠标（pointer:fine）不绑 mousedown/move：轻微位移会被误判为横拖，导致 click 打不开弹窗
const bindLifeDrag = (el: HTMLElement) => {
  el.addEventListener('touchstart', handleLifeTouchStart, { passive: true })
  el.addEventListener('touchmove', handleLifeTouchMove, { passive: true })
  el.addEventListener('touchend', handleLifeTouchEnd, { passive: true })
  const useMouseDrag = isTouchLikeDevice()
  if (useMouseDrag) {
    el.addEventListener('mousedown', handleLifeTouchStart)
    window.addEventListener('mousemove', handleLifeTouchMove)
    window.addEventListener('mouseup', handleLifeTouchEnd)
  }
}
const unbindLifeDrag = (el: HTMLElement | null) => {
  if (el) {
    el.removeEventListener('touchstart', handleLifeTouchStart)
    el.removeEventListener('touchmove', handleLifeTouchMove)
    el.removeEventListener('touchend', handleLifeTouchEnd)
    el.removeEventListener('mousedown', handleLifeTouchStart)
  }
  window.removeEventListener('mousemove', handleLifeTouchMove)
  window.removeEventListener('mouseup', handleLifeTouchEnd)
}

// 元素由 v-if + 异步数据控制，挂载时机不固定：用 watch 在元素真正出现/移除时绑定，避免固定延时绑空
watch(lifeViewportEl, (el, prevEl) => {
  unbindLifeDrag(prevEl ?? null)
  if (el) {
    bindLifeDrag(el)
    nextTick(() => updateLifeIndexLayout())
  }
})

onMounted(() => {
  window.addEventListener('resize', updateLifeIndexLayout)

  updateMobileIndicators()

  nextTick(() => {
    updateLifeIndexLayout()
    if (lifeViewportEl.value) bindLifeDrag(lifeViewportEl.value)
  })
})
onUnmounted(() => {
  window.removeEventListener('resize', updateLifeIndexLayout)
  unbindDebugPanel()
  unbindLifeDrag(lifeViewportEl.value)
  if (lifeScrollEndTimer) window.clearTimeout(lifeScrollEndTimer)
})

// 选中高亮 + 打开服务弹窗
const activeLifeId = ref<string | number | null>(null)
const selectLifeItem = (item: any) => {
  // 仅横滑拖动手势时忽略 click；滚动结束后的普通点击（含桌面鼠标）应正常打开
  if (lifeDragMoved) {
    lifeDragMoved = false
    return
  }
  activeLifeId.value = item?.index_type_id ?? null
  openService(item)
}
// 服务弹窗关闭时清除生活指数选中态，避免点开 → 关闭后还保留高亮
watch(serviceVisible, (visible) => {
  if (!visible) activeLifeId.value = null
})

// 热门城市（来自 /system/new/area/getHotCity，前 6 个）+ 并行拉各自当前天气
const hotCityList = ref<Array<{ id: number; mid: number; name: string }>>([])
const hotCityWeather = ref<Record<number, { temp: number | null; icon: number | null }>>({})

// 热门城市横向滚动控制
const hotCityScrollerRef = ref<HTMLElement | null>(null)
const hotCityCanPrev = ref(false)
const hotCityCanNext = ref(true)
const updateHotCityScroll = () => {
  const el = hotCityScrollerRef.value
  if (!el) return
  hotCityCanPrev.value = el.scrollLeft > 1
  hotCityCanNext.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
}
const scrollHotCity = (dir: number) => {
  const el = hotCityScrollerRef.value
  if (!el) return
  el.scrollBy({ left: dir * 200, behavior: 'smooth' })
}

let _hotCityLoading = false
const getHotCityList = async () => {
  if (_hotCityLoading || hotCityList.value.length) return
  _hotCityLoading = true
  try {
    const res = await $fetch<any>(`${config.public.baseURL}/system/new/area/getHotCity`, {
      method: 'GET',
      headers: { 'x-Language': 'zh-CN', 'Content-Type': 'application/json' }
    })
    const list = res?.data || []
    const top = list.slice(0, 7)
    hotCityList.value = top.map((c: any) => ({
      id: c.id,
      mid: c.mid,
      name: c.name?.replace(/市$/, '') || c.name
    }))
    // 温度与天气图标直接取自 getHotCity 返回（temp / icon），无需再逐城市请求
    const map: Record<number, { temp: number | null; icon: number | null }> = {}
    top.forEach((c: any) => {
      map[c.id] = {
        temp: c.temp ?? null,
        icon: c.icon ?? null
      }
    })
    hotCityWeather.value = map
    await nextTick()
    updateHotCityScroll()
  } catch (e) {
    console.error('[home] getHotCityList error:', e)
  } finally {
    _hotCityLoading = false
  }
}
const selectHotCity = async (hc: { id: number; mid: number; name: string }) => {
  userPickedCity = true
  weatherParams.cityId = hc.id
  weatherParams.lat = ''
  weatherParams.lon = ''
  await getCityData()
}

// ===== 副提示文案规则（前端生成）=====
const getWindTip = (level: any) => {
  const lv = Number(level)
  if (!lv) return ''
  if (lv <= 2) return t('home.windTip.calm')
  if (lv <= 4) return t('home.windTip.breeze')
  if (lv <= 6) return t('home.windTip.fresh')
  return t('home.windTip.strong')
}
const getHumidityTip = (h: any) => {
  const v = Number(h)
  if (isNaN(v)) return ''
  if (v < 30) return t('home.humidityTip.low')
  if (v > 70) return t('home.humidityTip.high')
  return t('home.humidityTip.normal')
}
const getUviTip = (u: any) => {
  const v = Number(u)
  if (isNaN(v)) return ''
  if (v >= 7) return t('home.uvTip.strong')
  if (v >= 3) return t('home.uvTip.moderate')
  return t('home.uvTip.weak')
}
const getVisTip = (v: any) => {
  const n = Number(v)
  if (isNaN(n)) return ''
  if (n < 5) return t('home.visTip.low')
  if (n < 10) return t('home.visTip.mid')
  return t('home.visTip.good')
}
const strengthConfig = computed(() => [
  { min: 0, max: 2, level: t('uvLevel.weakest') },
  { min: 3, max: 4, level: t('uvLevel.weak') },
  { min: 5, max: 6, level: t('uvLevel.moderate') },
  { min: 7, max: 9, level: t('uvLevel.strong') },
  { min: 10, max: 100, level: t('uvLevel.strongest') }
])
const getStrengthLevel = (value: number): string => {
  const m = strengthConfig.value.find((c) => value >= c.min && value <= c.max)
  return m?.level || ''
}

// ===== Hardcode 文案：媒体服务 / 行业解决方案 / VIP / 推广 =====
const promoStats = computed(() => [
  { num: t('home.stats.cityNum'), lbl: t('home.stats.cityLbl') },
  { num: t('home.stats.uptimeNum'), lbl: t('home.stats.uptimeLbl') },
  { num: t('home.stats.userNum'), lbl: t('home.stats.userLbl') },
  { num: t('home.stats.freshNum'), lbl: t('home.stats.freshLbl') }
])
const promoStatsRows = computed(() => [
  promoStats.value.slice(0, 2),
  promoStats.value.slice(2, 4)
])
const mediaList = computed(() => [
  { title: t('home.media.scene.title'), desc: t('home.media.scene.desc'), icon: staticURL('/images/media/scene.svg') },
  { title: t('home.media.splash.title'), desc: t('home.media.splash.desc'), icon: staticURL('/images/media/splash.svg') },
  { title: t('home.media.feed.title'), desc: t('home.media.feed.desc'), icon: staticURL('/images/media/feed.svg') },
  { title: t('home.media.brand.title'), desc: t('home.media.brand.desc'), icon: staticURL('/images/media/brand.svg') }
])
const solutionList = ref<Array<{ title: string; subTitle?: string; image: string; link?: string }>>([])
const solutionScrollerItems = computed(() => {
  const list = solutionList.value
  if (list.length <= 1) return list
  return [...list, list[0]]
})
const solutionIndex = ref(0)
const solutionScrollerRef = ref<HTMLElement>()
const solutionResetting = ref(false)
const solutionWrapping = ref(false)
const SOLUTION_ROTATE_MS = 3000
let solutionTimer: ReturnType<typeof setInterval> | null = null
let solutionWrapFallbackTimer: ReturnType<typeof setTimeout> | null = null
const clearSolutionWrapFallback = () => {
  if (solutionWrapFallbackTimer) {
    clearTimeout(solutionWrapFallbackTimer)
    solutionWrapFallbackTimer = null
  }
}
const scheduleSolutionWrapFallback = () => {
  clearSolutionWrapFallback()
  solutionWrapFallbackTimer = setTimeout(() => {
    solutionWrapFallbackTimer = null
    if (solutionWrapping.value) onSolutionScrollEnd()
  }, 500)
}
const resetSolutionToStart = () => {
  const el = solutionScrollerRef.value
  if (!el) return
  clearSolutionWrapFallback()
  solutionWrapping.value = false
  solutionResetting.value = true
  solutionIndex.value = 0
  el.scrollTo({ left: 0, behavior: 'auto' })
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      solutionResetting.value = false
    })
  })
}
const onSolutionScroll = () => {
  if (solutionResetting.value || solutionWrapping.value) return
  const el = solutionScrollerRef.value
  if (!el) return
  const w = el.clientWidth || 1
  const len = solutionList.value.length
  if (len <= 1) {
    solutionIndex.value = 0
    return
  }
  solutionIndex.value = Math.min(Math.round(el.scrollLeft / w), len - 1)
}
const onSolutionScrollEnd = () => {
  if (solutionResetting.value) return
  const el = solutionScrollerRef.value
  if (!el) return
  const w = el.clientWidth || 1
  const len = solutionList.value.length
  if (len <= 1) return
  if (Math.round(el.scrollLeft / w) < len) {
    solutionWrapping.value = false
    clearSolutionWrapFallback()
    return
  }
  resetSolutionToStart()
}
const scrollToSolution = (i: number) => {
  const el = solutionScrollerRef.value
  if (!el) return
  const len = solutionList.value.length
  if (len <= 1) {
    solutionIndex.value = 0
    return
  }
  const w = el.clientWidth || 1
  const prev = solutionIndex.value
  if (prev === len - 1 && i === 0) {
    solutionWrapping.value = true
    solutionIndex.value = 0
    el.scrollTo({ left: w * len, behavior: 'smooth' })
    scheduleSolutionWrapFallback()
    return
  }
  solutionWrapping.value = false
  solutionIndex.value = i
  el.scrollTo({ left: w * i, behavior: 'smooth' })
}
const stopSolutionRotate = () => {
  if (solutionTimer) {
    clearInterval(solutionTimer)
    solutionTimer = null
  }
  clearSolutionWrapFallback()
  solutionWrapping.value = false
}
const startSolutionRotate = () => {
  stopSolutionRotate()
  if (solutionList.value.length <= 1) return
  solutionTimer = setInterval(() => {
    const len = solutionList.value.length
    if (len <= 1) return
    const next = (solutionIndex.value + 1) % len
    scrollToSolution(next)
  }, SOLUTION_ROTATE_MS)
}
const getSolutionList = async () => {
  try {
    const res = await $fetch<any>('https://www.mojicb.com/eshop24-web/web/homepage/queryByPage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
      body: { type: 1 }
    })
    const list = Array.isArray(res?.data) ? res.data : []
    solutionList.value = list.slice(0, 4).map((it: any) => ({
      title: it.attributeName || '',
      subTitle: it.attributeSubtitle || '',
      image: it.pictureUrl || '',
      link: it.targetUrl ? `https://www.mojicb.com/solution/${it.router || ''}` : ''
    }))
    nextTick(() => {
      solutionWrapping.value = false
      solutionResetting.value = false
      solutionIndex.value = 0
      solutionScrollerRef.value?.scrollTo({ left: 0, behavior: 'auto' })
      startSolutionRotate()
    })
  } catch (e) {
    console.error('[home] getSolutionList error:', e)
  }
}
const vipTags = computed(() => [
  t('home.vipTags.minute'),
  t('home.vipTags.typhoon'),
  t('home.vipTags.fortyDay')
])

// ===== 处理每日预报：补 displayDate / formattedDate / 颜色 =====
const processDailyForecast = (dailyData: any[]) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const weekdays = [0, 1, 2, 3, 4, 5, 6].map((i) => t(`weekdays.${i}`))
  return dailyData.map((item) => {
    const fd = new Date(item.predict_date)
    fd.setHours(0, 0, 0, 0)
    const diff = Math.floor((fd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    let displayDate = ''
    if (diff === -1) displayDate = t('weather.yesterday')
    else if (diff === 0) displayDate = t('weather.today')
    else if (diff === 1) displayDate = t('weather.tomorrow')
    else if (diff === 2) displayDate = t('weather.afterTomorrow')
    else displayDate = weekdays[fd.getDay()]
    const m = (fd.getMonth() + 1).toString().padStart(2, '0')
    const d = fd.getDate().toString().padStart(2, '0')
    return { ...item, displayDate, formattedDate: `${m}/${d}` }
  })
}

/** 按时间字段匹配 AQI 数据，避免 hourly/aqi_forecast_hourly 数组长度或起点不同导致错位
 *  type=1：daily.predict_date ↔ aqi_forecast.predict_time（纯日期 "2026-05-21"）
 *  type=2：hourly.predict_time ↔ aqi_forecast_hourly.predict_time（datetime "2026-05-22 13:00:00"） */
const addQualityInfoToDaily = (data: any[], type: 1 | 2) => {
  const src = type === 1 ? aqi_forecast.value : aqi_forecast_hourly.value
  const aqiMap = new Map((src || []).map((a: any) => [a.predict_time, a.aqi]))
  const keyOf = (d: any) => (type === 1 ? d.predict_date : d.predict_time)
  return data.map((d) => {
    const aqi = aqiMap.get(keyOf(d))
    const info = aqi != null ? getAQIInfo(+aqi, nuxtApp as any) : { level: '', color: '', text: '' }
    return {
      ...d,
      aqi,
      quality: info.level,
      qualityColor: info.color,
      qualityText: info.text,
      color: info.color
    }
  })
}

// ===== 天气数据写入（SSR / 客户端共用）=====
const applyCityPayload = (city: any, persistCache = false) => {
  currentCity.value = city
  cityLon.value = String(city?.longtitude ?? '')
  cityLat.value = String(city?.latitude ?? '')
  weatherParams.lat = ''
  weatherParams.lon = ''
  weatherParams.cityId = city?.id
  if (persistCache) {
    safeStorageSetJSON('selectCity', {
      ...city,
      dataLanguage: locale.value,
      userPicked: userPickedCity,
    })
  }
}

const applyWeatherPayload = (w: any) => {
  try {
  if (!w?.current) {
    status.value = 1
    weatherReady.value = false
    weatherUiReady.value = false
    return
  }
  const refreshing = hasLoadedWeatherOnce.value && status.value === 0
  aqiData.value = w?.aqi
  aqi_forecast.value = w?.aqi_forecast || []
  aqi_forecast_hourly.value = w?.aqi_forecast_hourly || []
  currentData.value = w?.current || ({} as any)
  indexData.value = w?.currentIndex || []
  nextTick(() => updateLifeIndexLayout())
  alertList.value = Array.isArray(w?.alerts) ? w.alerts : []
  alertIndex.value = 0
  alertTrackResetting.value = false
  startAlertRotate()
  try {
    daily.value = w?.daily?.length ? processDailyForecast(w.daily) : []
    if (daily.value.length && aqi_forecast.value.length) {
      daily.value = addQualityInfoToDaily(daily.value, 1)
    }
  } catch (e) {
    console.error('[home] process daily error:', e)
    daily.value = []
  }
  try {
    hourly.value = w?.hourly?.length ? processDailyForecast(w.hourly) : []
    hourly.value = addQualityInfoToDaily(hourly.value, 2)
  } catch (e) {
    console.error('[home] process hourly error:', e)
    hourly.value = []
  }
  status.value = 0
  weatherReady.value = true
  hasLoadedWeatherOnce.value = true
  if (!refreshing) {
    weatherUiReady.value = false
    nextTick(() => {
      weatherUiReady.value = true
    })
  }
  } catch (e) {
    console.error('[home] applyWeatherPayload error:', e)
    status.value = 2
    weatherReady.value = false
    weatherUiReady.value = false
  }
}

const weatherApiHeaders = () => ({
  'x-Language': locale.value,
  'Content-Type': 'application/json'
})

const getCityData = async () => {
  try {
    if (!hasLoadedWeatherOnce.value) {
      weatherReady.value = false
    }
    const res = await $fetch<any>(`${config.public.baseURL}${cmsurlStrategy}/getCityData`, {
      method: 'GET',
      params: { lat: weatherParams.lat, lon: weatherParams.lon, cityId: weatherParams.cityId },
      headers: weatherApiHeaders()
    })
    if (res?.rc?.c !== 0) {
      status.value = 1
      weatherReady.value = false
      return
    }
    applyCityPayload(res?.data?.city, true)
    await getWeather()
  } catch (e) {
    console.error('[home] getCityData error:', e)
    status.value = 2
    weatherReady.value = false
  }
}

const getWeather = async () => {
  if (!weatherParams.cityId) {
    status.value = 1
    weatherReady.value = false
    return
  }
  try {
    const res = await $fetch<any>(`${config.public.baseURL}${cmsurlStrategy}/getCityWeatherAndIndex`, {
      method: 'GET',
      params: { lat: weatherParams.lat, lon: weatherParams.lon, cityId: weatherParams.cityId },
      headers: weatherApiHeaders()
    })
    if (res?.rc?.c !== 0) {
      status.value = 2
      weatherReady.value = false
      return
    }
    applyWeatherPayload(res?.data)
  } catch (e) {
    console.error('[home] getWeather error:', e)
    status.value = 2
    weatherReady.value = false
  }
}

// 资讯
const newsParams = reactive<NewsListParams>({
  pageNo: 1,
  pageSize: 3,
  isShowHomePage: 1
})
const getNews = async () => {
  try {
    const res = await $fetch<any>(`${config.public.baseURL}${cmsurlStrategy}/getNewInformationPage`, {
      method: 'GET',
      params: newsParams,
      headers: { 'x-Language': locale.value, 'Content-Type': 'application/json' }
    })
    newslist.value = res?.data?.list || []
  } catch (e) {
    console.error('[home] getNews error:', e)
  }
}

// SEO（与 weather/APP 一致：setup 顶层 SSR 拉取，供爬虫读到首屏 meta）
const seo = ref<configResponse['pageValue']['seo']>({
  title: '',
  keywords: '',
  desc: ''
})
const seoParams = reactive<configParams>({
  pageCode: 'HOME'
})
const getSeo = async () => {
  try {
    const { data } = await useServerRequest<configResponse>('HOME', `${cmsurlStrategy}/getPageConfig`, {
      method: 'GET',
      params: seoParams,
      language: locale.value
    })
    const getInfoData = data?.value?.data?.pageValue
    if (getInfoData?.seo) {
      seo.value = getInfoData.seo
    }
  } catch (e) {
    console.error('[home] getSeo error:', e)
  }
}
getSeo()

useHead({
  title: () => seo?.value?.title || '',
  meta: [
    {
      name: 'keywords',
      content: () => seo?.value?.keywords || ''
    },
    {
      name: 'description',
      content: () => seo?.value?.desc || ''
    }
  ],
  bodyAttrs: { class: 'home-body' }
})

// ===== 事件 =====
const openCityPicker = () => {
  cityVisible.value = true
  cityRef.value?.getProvincedata?.()
  cityRef.value?.getHotCity?.()
}
const setCityInfo = async (idString: { id: number; mid: number }) => {
  userPickedCity = true
  if (!hasLoadedWeatherOnce.value) {
    weatherReady.value = false
  }
  weatherParams.cityId = idString?.id
  weatherParams.lat = ''
  weatherParams.lon = ''
  if (weatherParams.cityId) {
    await getCityData()
  } else {
    status.value = 1
    weatherReady.value = false
  }
}

const loadDefaultCityWeather = async () => {
  weatherParams.cityId = DEFAULT_CITY_ID
  weatherParams.lat = ''
  weatherParams.lon = ''
  await getCityData()
}

const resolveWeatherByGeolocation = async () => {
  // 首页自动定位强制获取设备当前坐标，避免沿用 user_lnglat 中的历史位置
  const ll = await getBrowserLngLat(true).catch(() => null)
  if (userPickedCity) return
  if (ll?.lat && ll?.lng) {
    weatherParams.lat = ll.lat as any
    weatherParams.lon = ll.lng as any
    weatherParams.cityId = undefined as any
    awaitingDeviceRadarLocation = true
    try {
      await getCityData()
      // 天气接口按归属城市返回；雷达首次定位使用浏览器提供的原始设备坐标
      cityLon.value = String(ll.lng)
      cityLat.value = String(ll.lat)
    } finally {
      awaitingDeviceRadarLocation = false
      initRadarSrc()
    }
    return
  }
  await loadDefaultCityWeather()
}
const openAqi = () => (aqiVisible.value = true)
const openService = (item: WeatherIndex) => {
  serviceItem.value = item
  serviceVisible.value = true
}

// 联系我们
const contactPopUpRef = ref<any>(null)
const popUpSwitch = (visible: boolean) => {
  contactPopUpRef.value?.dialogSwitch(visible)
}
const openBusinessSite = () => {
  window.open('https://www.mojicb.com/', '_blank', 'noopener,noreferrer')
}

/** 访问首页埋点；SDK 空闲加载，需等 _bury 就绪 */
const trackHomePageView = () => {
  sendBuryClient({
    key: 'home_page_sw',
    name: '首页',
    module: 'header',
  })
}
const waitBuryThenTrackHome = () => {
  if (typeof window === 'undefined') return
  const buryWin = window as Window & { _bury?: unknown }
  if (buryWin._bury) {
    trackHomePageView()
    return
  }
  let tries = 0
  const timer = window.setInterval(() => {
    tries += 1
    if (buryWin._bury) {
      window.clearInterval(timer)
      trackHomePageView()
    } else if (tries >= 50) {
      window.clearInterval(timer)
    }
  }, 200)
}

// ===== 启动：等真实城市天气就绪后再展示（不先出北京兜底/背景）=====
onMounted(async () => {
  syncIpadLayoutClass()
  waitBuryThenTrackHome()
  onPageScroll()
  window.addEventListener('scroll', onPageScroll, { passive: true })

  if (compatFeatures.value.forceHomeShell) {
    weatherReady.value = true
    status.value = 0
    weatherUiReady.value = true
    hasLoadedWeatherOnce.value = true
    currentCity.value = { name: '基础版（未请求 API）', enname: 'Basic (no API)' }
    return
  }

  if (!compatFeatures.value.weatherApi) return

  if (compatFeatures.value.extras) {
    getNews()
    getHotCityList()
    getSolutionList()
  }

  try {
    const parsed = safeStorageGetJSON<{
      id?: number
      dataLanguage?: string
      longtitude?: string | number
      latitude?: string | number
      userPicked?: boolean
    }>('selectCity')
    if (parsed && parsed.userPicked === true && parsed.dataLanguage === locale.value && parsed.id) {
      currentCity.value = parsed
      cityLon.value = String(parsed.longtitude ?? '')
      cityLat.value = String(parsed.latitude ?? '')
      weatherParams.cityId = parsed.id
      await getWeather()
    } else {
      await resolveWeatherByGeolocation()
    }
  } catch (e) {
    console.error('[home] onMounted fatal error:', e)
    try {
      await loadDefaultCityWeather()
    } catch (e2) {
      console.error('[home] fallback to Beijing also failed:', e2)
      status.value = 2
      weatherReady.value = false
    }
  }
})
</script>
